import {
	addDays,
	endOfMonth,
	endOfYear,
	format,
	isSameDay,
	isSameMonth,
	isSameYear,
	isValid,
	startOfMonth,
	startOfWeek,
	startOfYear,
} from "date-fns";
import { t } from "i18next";

import { TDatesFilter } from "@/types";

/**
 * A calendar day ("2026-10-07", or the date part of a longer string) at
 * local midnight. `new Date("2026-10-07")` reads it as UTC midnight: the
 * day before for anyone west of Greenwich (a month boundary, an invoice
 * due date, the calendar's selected day all shifted by one).
 */
export const parseDay = (value: string): Date => {
	const [year, month, day] = value.slice(0, 10).split("-").map(Number);
	return new Date(year, month - 1, day);
};

/**
 * A period in words, as short as it reads: "5 March 2026", "March 2026",
 * "5 – 18 March 2026", "2026", "December 2025 – January 2026"... Months
 * in the UI's language (date-fns' default locale follows it). Same rules
 * as the report PDF's title (pdf-generator's formatSmartRange).
 */
export const formatSmartRange = (_dateFrom = "", _dateTo = _dateFrom) => {
	const day = (value: string) => (value ? parseDay(value) : new Date(NaN));
	const dateFrom = isValid(day(_dateFrom)) ? day(_dateFrom) : new Date();
	const dateTo = isValid(day(_dateTo)) ? day(_dateTo) : dateFrom;

	const startsMonth = isSameDay(dateFrom, startOfMonth(dateFrom));
	const endsMonth = isSameDay(dateTo, endOfMonth(dateTo));
	const startsYear = isSameDay(dateFrom, startOfYear(dateFrom));
	const endsYear = isSameDay(dateTo, endOfYear(dateTo));

	const d1 = format(dateFrom, "d");
	const d2 = format(dateTo, "d");
	const month1 = format(dateFrom, "MMMM");
	const month2 = format(dateTo, "MMMM");
	const year1 = format(dateFrom, "yyyy");
	const year2 = format(dateTo, "yyyy");

	if (isSameDay(dateFrom, dateTo))
		return t("dates.range.sameDay", {
			day: d1,
			month: month1,
			year: year1,
		});

	if (isSameMonth(dateFrom, dateTo))
		return startsMonth && endsMonth
			? t("dates.range.sameMonthFull", { month: month1, year: year1 })
			: t("dates.range.sameMonth", {
					d1,
					d2,
					month: month1,
					year: year1,
				});

	if (isSameYear(dateFrom, dateTo)) {
		if (startsYear && endsYear)
			return t("dates.range.sameYearFull", { year: year1 });
		return startsMonth && endsMonth
			? t("dates.range.sameYearMonths", { month1, month2, year: year1 })
			: t("dates.range.sameYear", {
					d1,
					d2,
					month1,
					month2,
					year: year1,
				});
	}

	if (startsYear && endsYear) return t("dates.range.years", { year1, year2 });
	return startsMonth && endsMonth
		? t("dates.range.crossYearMonths", { month1, month2, year1, year2 })
		: t("dates.range.crossYear", { d1, d2, month1, month2, year1, year2 });
};

// get the current month range based on the current date
export const getMonthRange = (date: Date = new Date()): TDatesFilter => {
	const firstDayOfMonth = startOfMonth(date);
	const lastDayOfMonth = endOfMonth(date);

	return {
		dateFrom: format(firstDayOfMonth, "yyyy-MM-dd"),
		dateTo: format(lastDayOfMonth, "yyyy-MM-dd"),
	};
};

// get all the days displayed in the calendar with a fixed number of 6 weeks
export const getDisplayedMonthRange = (
	date: Date = new Date()
): TDatesFilter => {
	const firstDayOfMonth = startOfMonth(date);
	// Find the Monday of the week that includes the first day of the month
	const firstDay = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });

	// Calculate the last day by adding 6 weeks (42 days) to the first day
	const lastDay = addDays(firstDay, 6 * 7 - 1);

	return {
		dateFrom: format(firstDay, "yyyy-MM-dd"),
		dateTo: format(lastDay, "yyyy-MM-dd"),
	};
};

export const isFullMonth = (dateStart: string, dateEnd: string): boolean => {
	const start = new Date(dateStart);
	const end = new Date(dateEnd);

	return (
		start.getDate() === 1 &&
		end.getDate() ===
			new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
	);
};

export const convertToDecimalHours = (timeString: string = "00:00") => {
	const [hours, minutes] = timeString.split(/[h:]/).map(Number);
	const decimalHours = hours + minutes / 60;
	return decimalHours;
};

export const convertToTime = (decimalHours = 0) => {
	// Extract absolute value and sign of the input
	const sign = Math.sign(decimalHours);
	const absDecimalHours = Math.abs(decimalHours);

	// Calculate hours and minutes
	const hours = Math.floor(absDecimalHours);
	const minutes = Math.round((absDecimalHours - hours) * 60)
		.toString()
		.padStart(2, "0");

	// Format hours with padding
	const paddedHours = hours.toString().padStart(2, "0");

	// Apply the sign back to the formatted string
	return `${sign < 0 ? "-" : ""}${paddedHours}h${minutes}`;
};
